import { PlusOutlined } from "@ant-design/icons";
import { Avatar, Button, Collapse, message, Modal, Spin, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import React, { useEffect, useState } from "react";

import { CoreResource, t } from "shared/plugins/localization";
import {
  formatMoney,
  redirectFileLocalUrl,
} from "shared/plugins/string-helper";
import { ProductCategoryModel } from "states/product-category/product-category.models";
import {
  ProductModel,
  ProductProductAttributeGroupModel,
} from "states/product/product.models";
import { Link } from "react-router-dom";
import {
  addProductApi,
  searchListProductApi,
  updateProductApi,
} from "states/product/product.api";
import { ProductInformationTab } from "./product-information-tab";
import { ProductName } from "shared/components/product/product-name";

export interface IProductUnitProductsProps {
  mainProduct: ProductModel;
  productCategories: ProductCategoryModel[];
  isUpdateMode: boolean;
  refresh: () => void;
}
interface IProductUnitProductModal {
  openModal: boolean;
  closeModal: () => void;
  model: ProductModel;
  isUpdateMode: boolean;
  onChange: (newModel: ProductModel) => void;
}
export function ProductUnitProducts(props: IProductUnitProductsProps) {
  const { mainProduct, productCategories, isUpdateMode, refresh } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [unitProducts, setUnitProducts] = useState<ProductModel[]>([]);
  const [unitProductModal, setUnitProductModal] =
    useState<IProductUnitProductModal>({
      openModal: false,
      isUpdateMode: false,
      onChange: (newModel: ProductModel) => {
        setUnitProductModal({
          ...unitProductModal,
          openModal: true,
          isUpdateMode: newModel?.id ? true : false,
          model: newModel,
        });
      },
      closeModal: () => {
        setUnitProductModal({
          ...unitProductModal,
          openModal: false,
          model: {} as ProductModel,
        });
      },
      model: {} as ProductModel,
    } as IProductUnitProductModal);

  const handleAddUnit = async () => {
    setUnitProductModal({
      ...unitProductModal,
      openModal: true,
      model: {
        isInventory: mainProduct.isInventory,
        name: mainProduct.name,
        mainCategoryId: mainProduct.mainCategoryId,
        relatedCategoryIds: mainProduct.relatedCategoryIds,
        isActive: mainProduct.isActive,
        images: mainProduct.images,
        isVariation: mainProduct.isVariation,
        variationAttributes: mainProduct.variationAttributes,
        attributeGroups: [] as ProductProductAttributeGroupModel[],
      } as ProductModel,
      isUpdateMode: false,
    });
  };

  const onSaved = async () => {
    setLoading(true);
    try {
      const payload = {
        id: unitProductModal.model.id,
        attributeGroups: unitProductModal.model.attributeGroups,
        images: unitProductModal.model.images,
        name: mainProduct.name,
        skuCode:
          unitProductModal.isUpdateMode || unitProductModal.model.skuCode !== ""
            ? unitProductModal.model.skuCode
            : `${mainProduct.skuCode}_${
                Number(mainProduct.unitProducts?.length) + 1
              }`,
        slug: "", //Tự gen ở BE
        variationAttributes: unitProductModal.model.variationAttributes,
        accessoryIds: unitProductModal.model.accessoryIds,
        barCode: unitProductModal.model.barCode,
        costPrice: unitProductModal.model.costPrice,
        customizeTheme: unitProductModal.model.customizeTheme,
        isAccessory: unitProductModal.model.isAccessory,
        isActive: unitProductModal.model.isActive,
        isInventory: unitProductModal.model.isInventory,
        mainCategoryId: mainProduct.mainCategoryId, //tự gen ở be
        mainVariationProductId: mainProduct.mainVariationProductId,
        price: unitProductModal.model.price,
        mainUnitProductId: mainProduct.id,
        promotionFromDate: unitProductModal.model.promotionFromDate,
        promotionPrice: unitProductModal.model.promotionPrice,
        promotionToDate: unitProductModal.model.promotionToDate,
        relatedCategoryIds: mainProduct.relatedCategoryIds, //tự gen ở be
        seoFields: unitProductModal.model.seoFields,
        unitId: unitProductModal.model.unit?.id,
        unitRate: unitProductModal.model.unitRate,
        size: unitProductModal.model.size,
        isModifier: false,
        branchId: mainProduct.branchId,
        tagIds: mainProduct.tags.map((m) => m.id),
      };
      if (unitProductModal.isUpdateMode) {
        await updateProductApi(payload);
      } else {
        await addProductApi(payload);
      }
      setLoading(false);
      message.success(t(CoreResource, "UpdateDataSuccessfully"));
      refresh();
    } catch (error) {
      message.error(
        error?.response?.data?.error?.message ??
          t(CoreResource, "DefaultErrorMessage")
      );
      setLoading(false);
    }
  };

  const handleEditUnit = async (editedProduct: ProductModel) => {
    setUnitProductModal({
      ...unitProductModal,
      openModal: true,
      isUpdateMode: true,
      model: editedProduct,
    });
  };

  const columns: ColumnsType<ProductModel> = [
    {
      title: "Sku code",
      dataIndex: "skuCode",
      key: "skuCode",
      width: "150px",
      render: (skuCode: string, item) => skuCode,
    },
    {
      title: "Product name",
      dataIndex: "websiteName",
      key: "websiteName",
      render: (websiteName: string, item) => (
        <React.Fragment>
          <Link
            to="#"
            onClick={() => {
              handleEditUnit(item);
            }}
          >
            <Avatar
              src={redirectFileLocalUrl(
                item.images.length > 0 ? item.images[0] : ""
              )}
              shape="square"
            />{" "}
            {<ProductName product={item} />}
          </Link>
        </React.Fragment>
      ),
    },
    {
      title: "Unit Measurement Rate",
      dataIndex: "unitRate",
      key: "unitRate",
      align: "center",
      render: (unitRate: number, item) => unitRate,
    },
    {
      title: "Selling price",
      dataIndex: "websiteName",
      key: "websiteName",
      align: "center",
      render: (websiteName: string, item) => formatMoney(item.price),
    },
  ];

  useEffect(() => {
    if (mainProduct && isUpdateMode && mainProduct.unitProducts.length > 0) {
      setLoading(true);
      searchListProductApi({
        page: 1,
        pageSize: 20,
        includeAccessory: true,
        includeChildrenUnitProduct: true,
        includeChildrenVariationProduct: true,
        productIds: mainProduct.unitProducts.map((m) => m.id),
      }).then((res) => {
        setLoading(false);
        setUnitProducts([...res.data]);
      });
    }
  }, [mainProduct, isUpdateMode]);

  return (
    <>
      {isUpdateMode && (
        <Spin spinning={loading}>
          <Collapse
            bordered={true}
            className="mt-4 border-radius-primary "
            defaultActiveKey={["1"]}
          >
            <Collapse.Panel header="Đơn vị tính" key="1">
              <Table
                bordered
                size="small"
                className="mb-4"
                columns={columns}
                pagination={false}
                dataSource={unitProducts}
                rowKey={(_record, index) => index?.toString() ?? ""}
              />
              <div className="mb-4">
                <Button onClick={handleAddUnit} icon={<PlusOutlined />}>
                  Thêm đơn vị tính
                </Button>
              </div>
            </Collapse.Panel>
          </Collapse>
          <Modal
            title={
              unitProductModal?.isUpdateMode
                ? `Cập nhật đơn vị tính`
                : `Thêm đơn vị tính`
            }
            width={1000}
            visible={unitProductModal.openModal}
            onOk={onSaved}
            confirmLoading={loading}
            onCancel={unitProductModal.closeModal}
          >
            <ProductInformationTab
              isUnitProduct={true}
              model={unitProductModal.model}
              onChange={unitProductModal.onChange}
              productCategories={productCategories}
              isUpdateMode={unitProductModal?.isUpdateMode}
            />
          </Modal>
        </Spin>
      )}
    </>
  );
}
